// One place that notices a dead session (#284), however a call finds out:
//   - the API answers 401 with `code: SESSION_EXPIRED` (its token verifier
//     refused the token: expired, revoked, or signed out elsewhere);
//   - the API answers 403 with `code: ACCOUNT_DISABLED` (the account, or the
//     team owner's account, was disabled in the BasiQR Console, #466);
//   - the API answers 403 with `code: SEAT_SUSPENDED` (a team member over the
//     owner's plan seats, #459);
//   - there is no token left to send (the refresh token itself expired).
// It fires SESSION_EXPIRED_EVENT; AuthContext signs out and the login page
// says why. Other 401s (wrong password on a re-auth, a public link that needs
// verification) are not session expiry and are left to their callers.

export const SESSION_EXPIRED_EVENT = 'basiqr:session-expired';
export const SESSION_EXPIRED_CODE = 'SESSION_EXPIRED';
export const ACCOUNT_DISABLED_CODE = 'ACCOUNT_DISABLED';
export const SEAT_SUSPENDED_CODE = 'SEAT_SUSPENDED';
const NOTICE_KEY = 'basiqr:session-expired-notice';

/**
 * Why a session ended: the API refused it, 24 h without activity (#284), the
 * account was disabled (#466), or the member's seat is over the plan (#459).
 */
export type SessionEndReason = 'expired' | 'idle' | 'disabled' | 'suspended';

const isNamedReason = (value: unknown): value is Exclude<SessionEndReason, 'expired'> =>
  value === 'idle' || value === 'disabled' || value === 'suspended';

export function reportSessionExpired(reason: SessionEndReason = 'expired'): void {
  window.dispatchEvent(new CustomEvent<SessionEndReason>(SESSION_EXPIRED_EVENT, { detail: reason }));
}

/** The reason a SESSION_EXPIRED_EVENT carries ('expired' when it carries none). */
export function sessionEndReasonOf(event: Event): SessionEndReason {
  const detail = (event as CustomEvent<unknown>).detail;
  return isNamedReason(detail) ? detail : 'expired';
}

/** Leave a note for the login page, which shows it once. */
export function markSessionExpiredNotice(reason: SessionEndReason = 'expired'): void {
  try {
    sessionStorage.setItem(NOTICE_KEY, reason);
  } catch {
    /* storage unavailable: the user just lands on the login page */
  }
}

/** Why the login page should say the session ended, or null. */
export function sessionEndNotice(): SessionEndReason | null {
  try {
    const value = sessionStorage.getItem(NOTICE_KEY);
    return isNamedReason(value) ? value : value ? 'expired' : null;
  } catch {
    return null;
  }
}

/** Whether the login page should say the session ended. */
export function hasSessionExpiredNotice(): boolean {
  return sessionEndNotice() !== null;
}

/** The login page showed it: don't show it again. */
export function clearSessionExpiredNotice(): void {
  try {
    sessionStorage.removeItem(NOTICE_KEY);
  } catch {
    /* nothing to clear */
  }
}

const requestUrl = (input: RequestInfo | URL): string =>
  typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

/**
 * Wrap window.fetch once: a `401 SESSION_EXPIRED` or a `403 ACCOUNT_DISABLED`
 * from the BasiQR API (any URL under `apiBase`) reports the session as ended.
 * The response is passed on unchanged, so callers still see and handle it.
 */
export function installSessionExpiryInterceptor(apiBase: string): void {
  const marker = '__basiqrSessionExpiry';
  if (!apiBase || (window.fetch as any)[marker]) return;
  const originalFetch = window.fetch.bind(window);

  const wrapped = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await originalFetch(input, init);
    if ((response.status === 401 || response.status === 403) && requestUrl(input).startsWith(apiBase)) {
      const status = response.status;
      response
        .clone()
        .json()
        .then((body) => {
          if (status === 401 && body?.code === SESSION_EXPIRED_CODE) reportSessionExpired('expired');
          else if (status === 403 && body?.code === ACCOUNT_DISABLED_CODE) reportSessionExpired('disabled');
          else if (status === 403 && body?.code === SEAT_SUSPENDED_CODE) reportSessionExpired('suspended');
        })
        .catch(() => undefined);
    }
    return response;
  };
  (wrapped as any)[marker] = true;
  window.fetch = wrapped as typeof window.fetch;
}
